import { DistributiveOmit, objectMap } from '@direct-frontend/stdlib';

import { FieldApi, FieldApiOptionsContainer, createFieldApi } from '../fields/fieldApi';
import { BaseFields, InferFieldsValue } from '../fields/types';
import { ValidationOptions } from '../fields/validation';
import { getFormStoreSingleton } from '../store/store';
import { BaseSubmitError, BaseValidationError, ValidationState, ValidatorType } from '../types';
import { ValueContainer, createValueContainer } from '../utils/createValueContainer';

import { $internals as $formInternals, FormActionsMap } from './types';
import { assertFormInitialized } from './utils/assertFormInitialized';
import { scrollToFirstError } from './scrollToFirstError';

/** Ключ, за которым скрыты внутренние методы `formApi` */
export const $internals = Symbol('$internals');

/** Методы для работы с формой в валидаторах */
export type FormApi<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = never,
    FormContext = never,
> = {
    /** Идентификатор формы */
    id: string;
    /** Добавляет ошибку валидации в состояние формы */
    addValidationError: (error: DistributiveOmit<FormValidationError, 'meta'>) => void;
    /** Добавляет ошибки валидации в состояние формы */
    addValidationErrors: (errors: DistributiveOmit<FormValidationError, 'meta'>[]) => void;
    /** Возвращает текущее значение контекста формы */
    getContext: () => FormContext;
    /** Поля формы */
    fields: {
        [FieldName in keyof FormFields & string]: FieldApi<FormFields[FieldName]> & {
            hasValidationErrors: () => Promise<boolean>;
        };
    };
    /** Прокручивает страницу до первого поля с ошибкой */
    scrollToFirstError: () => void;
    /** Внутренние методы, которые не доступны в публичном API */
    [$internals]: {
        updateOptions: FormApiOptionsContainer['set'];
    };
};

/** Параметры работы объекта `formApi` */
type FormApiOptions = {
    /**
     * `formApi` имеет два типа хранилища с которыми он умеет работает:
     * 1. in-memory – все ошибки сохраняются в объект в памяти и читаются из него же
     *    Используется при выполнении синхронных валидаторов, чтобы оптимизировать
     *    сохранение ошибок в слайс и добавить их все за один экшен
     * 2. slice – все ошибки сохраняются сразу же в слайс и читаются из него же
     *    Используется при выполнение асинхронных валидаторов, чтобы каждый валидатор
     *    получал доступ к актуальному состоянию ошибок, иначе работа с временным объектом и момент
     *    записи в слайс будет неконтроллируемым
     */
    storageType: 'in-memory' | 'slice';
};

/**
 * Так как определить тип валидатора (синхронный или асинхронный) можно только после его вызова,
 * то параметры работы `formApi` хранятся в контейнере, который позволяет динамически изменять
 * параметры в уже созданных `formApi`
 */
type FormApiOptionsContainer = ValueContainer<FormApiOptions>;

// Возвращает `formApi` для формы
export function createFormApi<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validatorType: ValidatorType,
    validationOptions: Pick<
        ValidationOptions<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        'cause' | 'form'
    >,
    validationState: ValidationState<FormValidationError>
): FormApi<FormFields, FormValidationError, FormContext> {
    const formApiOptionsContainer: FormApiOptionsContainer = createValueContainer({ storageType: 'in-memory' });
    const { addFormValidationErrors, getFormContext } = getFormStoreSingleton(validationOptions.form.name);

    // Оборачивает родительский formApiOptionsContainer, чтобы в дочернем поле всегда иметь актуальный storageType
    function getInnerFieldApiOptionsContainer(): FieldApiOptionsContainer {
        return {
            set: () => {},
            get: () => ({
                storageType: formApiOptionsContainer.get().storageType,
                source: { type: 'form' },
            }),
        };
    }

    return {
        id: validationOptions.form.id,
        getContext: () => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });
            // Значения в сторе не типизированы, но мы гарантируем значение публичным интерфейсом типизированных инстансов
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см.выше
            return getFormContext({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            }) as FormContext;
        },
        addValidationError: (error: DistributiveOmit<FormValidationError, 'meta'>) => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            if (formApiOptionsContainer.get().storageType === 'in-memory') {
                // TypeScript считает, что тип может быть более узким и не содержать `form`, но на деле это не так
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                validationState.formValidationErrorsMap[validatorType].push({
                    ...error,
                    meta: { source: { type: 'form' } },
                } as FormValidationError);
                return;
            }

            // Если мы находимся в асинхронном валидаторе, то перед сохранением ошибки
            // нужно проверить не была ли валидация отменена
            const controller = validationState.formValidationMeta[validatorType].controller;
            if (controller?.signal.aborted) {
                return;
            }

            addFormValidationErrors({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                validatorType,
                validationErrors: [
                    {
                        ...error,
                        meta: { source: { type: 'form' } },
                    },
                ],
            });
        },

        addValidationErrors: (errors: DistributiveOmit<FormValidationError, 'meta'>[]) => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            // TypeScript считает, что тип может быть более узким и не содержать `form`, но на деле это не так
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            const errorsWithMeta = errors.map(error => ({
                ...error,
                meta: { source: { type: 'form' } },
            })) as FormValidationError[];

            if (formApiOptionsContainer.get().storageType === 'in-memory') {
                validationState.formValidationErrorsMap[validatorType].push(...errorsWithMeta);
                return;
            }

            // Если мы находимся в асинхронном валидаторе, то перед сохранением ошибки
            // нужно проверить не была ли валидация отменена
            const controller = validationState.formValidationMeta[validatorType].controller;
            if (controller?.signal.aborted) {
                return;
            }

            addFormValidationErrors({
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
                validatorType,
                validationErrors: errorsWithMeta,
            });
        },

        // Так как FieldApi – Conditional Type, то TS теряет связь между конкретными инстансами и общим типом
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
        fields: objectMap(validationOptions.form[$formInternals].fields, (field, fieldName) =>
            createFieldApi(
                validatorType,
                {
                    ...validationOptions,
                    field,
                    fieldId: fieldName,
                },
                validationState,
                getInnerFieldApiOptionsContainer()
            )
        ) as FormApi<FormFields, FormValidationError>['fields'],

        scrollToFirstError: () => {
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });
            scrollToFirstError({ form: validationOptions.form });
        },

        [$internals]: {
            updateOptions: formApiOptionsContainer.set,
        },
    };
}

/** Методы для работы с формой в onSubmit */
export interface SubmitFormApi<
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError = never,
    FormContext = never,
    FormContextInput = never,
> extends FormApi<FormFields, FormValidationError, FormContext> {
    /**
     * Переинициализирует форму
     * @param formValue - значение формы. Если не передано, то используются значения по умолчанию из полей
     * @param context - значение контекста формы. Если не передано, то используются текущее значение контекста
     */
    reinitialize: (formValue?: InferFieldsValue<FormFields>, context?: FormContextInput) => void;

    /**
     * Добавляет ошибку, которая будет возвращена в onSubmit
     * @param error - ошибка
     */
    addSubmitError: (error: BaseSubmitError) => void;
}

export function createSubmitFormApi<
    FormName extends string,
    FormFields extends BaseFields,
    FormValidationError extends BaseValidationError,
    FormContextInput,
    FormContext,
    FormActions extends FormActionsMap<FormFields, FormValidationError, FormContext>,
    FormSubmitArg,
    FormSubmitReturnValue,
>(
    validatorType: ValidatorType,
    validationOptions: Pick<
        ValidationOptions<
            FormName,
            FormFields,
            FormValidationError,
            FormContextInput,
            FormContext,
            FormActions,
            FormSubmitArg,
            FormSubmitReturnValue
        >,
        'cause' | 'form'
    >,
    validationState: ValidationState<FormValidationError>
): SubmitFormApi<FormFields, FormValidationError, FormContext, FormContextInput> {
    const formApi = createFormApi(validatorType, validationOptions, validationState);

    return {
        ...formApi,
        reinitialize: (formValue?: InferFieldsValue<FormFields>, contextArg?: FormContextInput) => {
            // В SubmitFormApi проверяем только тот факт что форма инициализирована
            assertFormInitialized({
                formName: validationOptions.form.name,
                formId: validationOptions.form.id,
                formInitializationId: validationOptions.form[$formInternals].formInitializationId,
            });

            validationOptions.form[$formInternals].reinitialize(formValue, contextArg);
        },
        addSubmitError: validationOptions.form.addSubmitError.bind(validationOptions.form),
    };
}
